import axios from "axios";
import config from "../../config";
import LocalStorageService from "../services/LocalStorageService";
import Utils from "../../utils";

const axiosClient = axios.create({
  headers: {
    "Content-Type": "application/json",
  },
  baseURL: process.env.REACT_APP_API_URL,
  withCredentials: true,
});

// 401s on these endpoints mean bad credentials / explicit logout, not an
// expired access token — never try to refresh or retry them.
const AUTH_EXEMPT_URLS = [
  `${config.API_URL}/auth/`,
  `${config.API_URL}/demo_auth/`,
  `${config.API_URL}/auth/logout/`,
];

let refreshPromise = null; // single-flight: concurrent 401s share one refresh call

// [CANA-654] Fired on window for every 403 so the app can resync the stored
// permissions (useApp.js) — a superuser may have changed the user's group
// since login. request.js cannot dispatch getMe itself: importing the store
// here would be circular.
export const FORBIDDEN_EVENT = "canasale:forbidden";

// Web Locks name shared by every tab of the app — only the holder may spend
// the single-use refresh token.
const REFRESH_LOCK_NAME = "canasale-token-refresh";

// How long to wait for a racing tab to persist its freshly rotated tokens
// before concluding the session is genuinely dead.
const ROTATION_GRACE_MS = 1000;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const getStoredAccess = () =>
  LocalStorageService.get("authorizationData")?.access;

const logout = () => {
  LocalStorageService.remove("authorizationData");
  if (window.location.pathname !== "/") {
    window.location.href = "/";
  }
};

// A 4xx from /auth/refresh/ (except 429) means the refresh token itself was
// rejected. Anything else — 429 throttle, 5xx, network blip — is transient
// and must never end the session.
const isAuthRejection = (error) => {
  const status = error?.response?.status;
  return status >= 400 && status < 500 && status !== 429;
};

const doRefresh = () => {
  // Read the refresh token at send time — a racing tab may have rotated it
  // since the caller was queued.
  const refresh = LocalStorageService.get("authorizationData")?.refresh;
  // Bare axios so the call never re-enters axiosClient's interceptors.
  return axios
    .post(
      `${config.API_URL}/auth/refresh/`,
      { refresh },
      { withCredentials: true }
    )
    .then((res) => {
      // The backend rotates the refresh token (single-use), so both fields
      // must be saved. Update them individually — authorizationData also
      // holds group_permissions / is_superuser read by Can.js.
      LocalStorageService.updateObject(
        "authorizationData",
        "access",
        res.data.access
      );
      LocalStorageService.updateObject(
        "authorizationData",
        "refresh",
        res.data.refresh
      );
      return res.data.access;
    });
};

export const refreshTokens = () => {
  if (!refreshPromise) {
    const startingAccess = getStoredAccess();
    const runExclusive = () => {
      // We may have queued behind another tab's refresh — if the stored
      // access token changed while we waited for the lock, reuse it instead
      // of spending the (already rotated) refresh token again.
      const currentAccess = getStoredAccess();
      if (currentAccess && currentAccess !== startingAccess) {
        return Promise.resolve(currentAccess);
      }
      return doRefresh();
    };
    // Cross-tab mutex: only one tab at a time may refresh. Falls back to the
    // per-tab single-flight guard where Web Locks is unavailable (jsdom, old
    // Safari) — the localStorage re-checks below still cover that case.
    refreshPromise = (
      navigator.locks?.request
        ? navigator.locks.request(REFRESH_LOCK_NAME, runExclusive)
        : runExclusive()
    ).finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
};

axiosClient.interceptors.request.use(
  (request) => {
    const token = LocalStorageService.get("authorizationData")?.access;
    if (request.url === `${config.API_URL}/images/`) {
      request.headers["Content-Type"] = 'multipart/form-data'
    }
    if (token) {
      request.headers.Authorization = `Bearer ${token}`;
      // Remember the exact token this request was sent with so the response
      // interceptor can detect cross-tab rotation without parsing headers.
      request._accessToken = token;
    }
    return request;
  },
  (error) => {
    return Promise.reject(error);
  }
);

axiosClient.interceptors.response.use(
  (response) => {
    if (response === undefined) {
      return Promise.reject(
        new Error("Incorrect response format, the server might be unavailable")
      );
    }
    return response.data;
  },
  async (error) => {
    const originalRequest = error.config;

    if (
      error.response?.status === 403 &&
      originalRequest?.url !== `${config.API_URL}/me/`
    ) {
      window.dispatchEvent(new Event(FORBIDDEN_EVENT));
    }

    if (error.response?.status === 401) {
      const isExempt = AUTH_EXEMPT_URLS.includes(originalRequest?.url);

      if (originalRequest && !originalRequest._retry && !isExempt) {
        originalRequest._retry = true; // at most one retry per request

        const failedToken = originalRequest._accessToken;
        const authData = LocalStorageService.get("authorizationData");

        if (
          authData?.access &&
          failedToken &&
          authData.access !== failedToken
        ) {
          // Another tab already refreshed — just retry with the new token.
          return axiosClient(originalRequest);
        }

        if (authData?.refresh) {
          try {
            await refreshTokens();
            return axiosClient(originalRequest);
          } catch (refreshError) {
            // Another tab may have consumed the single-use refresh token
            // while our call was in flight — re-check before giving up.
            let latestAccess = getStoredAccess();
            if (latestAccess && latestAccess !== failedToken) {
              return axiosClient(originalRequest);
            }
            // The winning tab may not have persisted the rotated tokens yet —
            // give it a moment and look again.
            await sleep(ROTATION_GRACE_MS);
            latestAccess = getStoredAccess();
            if (latestAccess && latestAccess !== failedToken) {
              return axiosClient(originalRequest);
            }
            if (isAuthRejection(refreshError)) {
              logout();
            } else {
              // Transient failure (429 throttle, 5xx, network blip on wake) —
              // one more attempt after the grace period, never a logout.
              try {
                await refreshTokens();
                return axiosClient(originalRequest);
              } catch (retryError) {
                if (isAuthRejection(retryError)) {
                  const recheckAccess = getStoredAccess();
                  if (recheckAccess && recheckAccess !== failedToken) {
                    return axiosClient(originalRequest);
                  }
                  logout();
                }
                // Still transient — keep the session; the request just fails
                // and the next 401 will trigger a fresh refresh attempt.
              }
            }
          }
        } else {
          logout();
        }
      } else if (!isExempt) {
        // The retried request 401'd again — the session is genuinely dead.
        logout();
      }
    }

    // Capitalize all error messages from backend
    if (error?.response?.data) {
      error.response.data = Utils.capitalizeErrorMessages(error.response.data);
    }

    return Promise.reject(error);
  }
);

export default axiosClient;
